import { format, parseISO } from 'date-fns';
import type {
  OpsBudgetPeriod,
  OpsMissionDirection,
  OpsMissionPrecondition,
  OpsMissionTier,
  OpsRunStatus,
} from './types';

export function fmtWhen(iso: string): string {
  try {
    return format(parseISO(iso), 'dd/MM HH:mm:ss');
  } catch {
    return '—';
  }
}

export function runStatusBadgeClass(status: OpsRunStatus): string {
  switch (status) {
    case 'ok':
      return 'bg-okBg text-ok';
    case 'error':
      return 'bg-dangerBg text-danger';
    default:
      return 'bg-infoBg text-info';
  }
}

export function runStatusLabel(status: OpsRunStatus): string {
  switch (status) {
    case 'ok':
      return 'concluído';
    case 'error':
      return 'erro';
    default:
      return 'em andamento';
  }
}

export function tierLabel(tier: OpsMissionTier): string {
  switch (tier) {
    case 'low':
      return 'baixo';
    case 'medium':
      return 'médio';
    case 'high':
      return 'alto';
    default:
      return 'nenhum (sempre propõe)';
  }
}

export function directionLabel(direction: OpsMissionDirection): string {
  switch (direction) {
    case 'up':
      return 'só aumenta';
    case 'down':
      return 'só reduz';
    default:
      return 'nenhuma (livre)';
  }
}

export function preconditionLabel(
  precondition: OpsMissionPrecondition
): string {
  switch (precondition) {
    case 'idle':
      return 'só com worker ocioso';
    case 'abandoned':
      return 'só com fila abandonada';
    default:
      return 'nenhuma (sempre roda)';
  }
}

export function periodLabel(period: OpsBudgetPeriod): string {
  switch (period) {
    case 'day':
      return 'dia';
    case 'week':
      return 'semana';
    case 'month':
      return 'mês';
    default:
      return period || '—';
  }
}

export function budgetLabel(budget: {
  maxTokens: number;
  maxActions: number;
  period: OpsBudgetPeriod;
}): string {
  const tokens =
    budget.maxTokens > 0 ? `${budget.maxTokens} tokens` : 'tokens ilimitados';
  const actions =
    budget.maxActions > 0 ? `${budget.maxActions} ações` : 'ações ilimitadas';
  return `${tokens} · ${actions} / ${periodLabel(budget.period)}`;
}

export function spentLabel(
  spent: { tokens: number; actions: number },
  budget: { maxTokens: number; maxActions: number; period: OpsBudgetPeriod }
): string {
  const tokens =
    budget.maxTokens > 0
      ? `${spent.tokens}/${budget.maxTokens} tokens`
      : `${spent.tokens} tokens (sem teto)`;
  const actions =
    budget.maxActions > 0
      ? `${spent.actions}/${budget.maxActions} ações`
      : `${spent.actions} ações (sem teto)`;
  return `${tokens} · ${actions} / ${periodLabel(budget.period)}`;
}

// actorLabel troca "mission:<id>" pelo nome da missão. O id cru não diz nada
// para quem lê a lista de runs, mas ele é o fallback: missão apagada continua
// tendo runs no histórico, e sem o id a linha ficaria órfã.
// isMissionRun separa run automático de run pedido por pessoa. O prefixo
// "mission:" vive aqui e no actorLabel, e em nenhum outro lugar.
export function isMissionRun(actor: string): boolean {
  return actor.startsWith('mission:');
}

export function actorLabel(actor: string, names: Map<string, string>): string {
  if (!actor.startsWith('mission:')) return actor;
  const id = actor.slice('mission:'.length);
  return names.get(id) ?? actor;
}
